import { useState, useEffect, FormEvent } from 'react';
import {
  getMemberships,
  createMembership,
  updateMembership,
  deleteMembership,
  getTeams,
  getCollectionsStatus,
  Membership,
  MembershipVisibility,
  CollectionsStatus,
  Team,
} from '../api/client';
import CollectionsNav from '../components/CollectionsNav';
import { describePlan, monthName, monthsBetween } from '../utils/charge';

/**
 * Collections → Memberships. The charge-plan catalog: named plans (e.g.
 * "U12 Training 2026/27", £150, monthly×10) scoped club-wide / by team /
 * by age group. Players are enrolled into a plan as per-player subscriptions
 * (the enrol wizard, next). Gated on features.collections.
 *
 * Top of page: payments connection status (a club must
 * connect + be verified before it can actually collect).
 */

// Four options, four behaviours. 'season' is deliberately NOT offered: it
// collects identically to 'one-off', so it was a second control for something
// the plan NAME already says. Every season plan on the platform was already
// built as one-off and called "Season 2026/27" — nobody ever picked it.
// Existing 'season' rows keep working (isOneOff covers both), so this needs no
// migration; it just stops offering the duplicate.
//
// Annual is the one that genuinely differs from a single payment, and its
// label has to say so — "Annual (once a year)" sitting next to "Per season
// (single charge)" is what made the distinction unreadable in the first place.

interface FormState {
  name: string;
  pricePounds: string;        // £ as entered; converted to pence on save. Per month when monthly.
  billing: 'once' | 'monthly';
  firstMonth: string;         // 'YYYY-MM'
  lastMonth: string;
  visibility: MembershipVisibility;
  teamId: string;
  ageGroup: string;
}

function emptyForm(): FormState {
  return { name: '', pricePounds: '', billing: 'once', firstMonth: '', lastMonth: '', visibility: 'admin-only', teamId: '', ageGroup: '' };
}

function membershipToForm(m: Membership): FormState {
  return {
    name: m.name,
    pricePounds: (m.totalPrice / 100).toFixed(2),
    billing: m.billing,
    firstMonth: m.firstPaymentMonth,
    lastMonth: m.lastPaymentMonth,
    visibility: m.visibility,
    teamId: m.teamId,
    ageGroup: m.ageGroup,
  };
}

/** This month and the next 18, as 'YYYY-MM'. */
function monthOptions(): string[] {
  const d = new Date();
  return Array.from({ length: 19 }, (_, i) => {
    const t = new Date(d.getFullYear(), d.getMonth() + i, 1);
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}`;
  });
}

export default function Memberships() {
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [gc, setGc] = useState<CollectionsStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Membership | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => { void load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const [ms, tm] = await Promise.all([getMemberships(), getTeams()]);
      setMemberships(ms);
      setTeams(tm);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load memberships');
    } finally {
      setLoading(false);
    }
    // Non-blocking: the page is usable while this resolves.
    getCollectionsStatus().then(setGc).catch(() => setGc({ connected: false, verified: false }));
  }


  function openCreate() { setEditing(null); setForm(emptyForm()); setShowModal(true); }
  function openEdit(m: Membership) { setEditing(m); setForm(membershipToForm(m)); setShowModal(true); }
  function closeModal() { setShowModal(false); setEditing(null); setForm(emptyForm()); }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) { setError('Name is required'); return; }
    const pence = Math.round(parseFloat(form.pricePounds) * 100);
    if (!Number.isFinite(pence) || pence <= 0) { setError('Enter a valid price'); return; }
    if (form.billing === 'monthly') {
      const n = monthsBetween(form.firstMonth, form.lastMonth);
      if (!form.firstMonth || !form.lastMonth) { setError('Choose the first and last payment months'); return; }
      if (n < 1) { setError('The last payment must be on or after the first'); return; }
      if (n > 12) { setError('A season can have at most 12 monthly payments'); return; }
    }
    try {
      setSaving(true);
      const payload: Partial<Membership> = {
        name: form.name.trim(),
        totalPrice: pence,
        billing: form.billing,
        ...(form.billing === 'monthly' ? { firstPaymentMonth: form.firstMonth, lastPaymentMonth: form.lastMonth } : {}),
        visibility: form.visibility,
        teamId: form.teamId || '',
        ageGroup: form.ageGroup.trim() || '',
      };
      if (editing) {
        await updateMembership(editing.membershipId, payload);
        setSuccess(`Updated ${form.name.trim()}`);
      } else {
        await createMembership(payload);
        setSuccess(`Created ${form.name.trim()}`);
      }
      await load();
      closeModal();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save membership');
    } finally {
      setSaving(false);
    }
  }

  async function handleArchive(m: Membership) {
    if (!confirm(`Archive "${m.name}"? Existing subscriptions keep running; it just won't be offered for new enrolments.`)) return;
    try {
      await deleteMembership(m.membershipId);
      setSuccess(`${m.name} archived`);
      await load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to archive membership');
    }
  }

  const teamName = (id: string) => teams.find(t => t.id === id)?.name || '';
  const visible = memberships.filter(m => showArchived || m.active);

  // Live "how this charges" preview for the form, from the current inputs.


  return (
    <div className="tm-page-shell">
      <CollectionsNav />
      <div className="page-head">
        <div>
          <h1>Memberships</h1>
          <p className="subtitle">
            Your charge plans. Set a fee for the whole club, a team or an age group, as one
            payment or monthly for the season, then enrol players to collect it by card.
          </p>
        </div>
        <div className="head-actions">
          <button className="ebtn ebtn-primary" onClick={openCreate}>+ New membership</button>
        </div>
      </div>

      {/* Payments connection status */}
      {gc && !gc.connected && (
        <div className="col-connect">
          <div className="col-connect-body">
            <p className="col-connect-title">Payments account not connected</p>
            <p className="col-connect-sub">
              Set up card payments under Settings → Payments. It's the same account the club
              shop uses, so if you already sell kit there's nothing more to do.
            </p>
          </div>
        </div>
      )}
      {/* Connected but not yet able to charge. This state did not exist while
          the API reported verified=!!accountId: a club that had merely started
          Stripe onboarding was shown "Ready to collect ✓". */}
      {gc && gc.connected && !gc.verified && (
        <div className="col-connect">
          <div className="col-connect-body">
            <p className="col-connect-title">Payment setup unfinished</p>
            <p className="col-connect-sub">
              {gc.statusUnavailable ? (
                <>We couldn't check your payment status just now. Try again in a minute —
                  nothing is wrong with your fee plans.</>
              ) : (
                <>Your Stripe account exists but can't take payments yet
                  {gc.requirementsDue ? <> — Stripe still needs <strong>{gc.requirementsDue} more details</strong></> : null}.
                  Finish it under Settings → Payments and this page updates on its own. You can set
                  your fee plans up now; nothing reaches parents until payments are live.</>
              )}
            </p>
          </div>
        </div>
      )}
      {gc && gc.connected && gc.verified && (
        <div className="col-connect is-connected">
          <div className="col-connect-body">
            <p className="col-connect-title">
              Ready to collect <span className="col-verified">✓</span>
            </p>
            {/* ONE panel. There used to be a second below this, in a box
                called col-soon because it began life as a "coming soon"
                banner; by the time Collections shipped it was repeating this
                one almost sentence for sentence. Do not add another — say it
                here or not at all, and do not re-advertise Direct Debit or
                subscriptions (see lambda/routes/stripe-collections.ts). */}
            <p className="col-connect-sub">
              Parents pay <strong>{gc.clubName || 'your club'}</strong> by card
              {gc.monthlyReady ? ', as one payment or monthly for the season' : ', one payment per fee'}.
              The money settles straight to your account and your club is named on the payment,
              not us. Card details go to Stripe; neither you nor we ever see them.
            </p>
          </div>
        </div>
      )}

      {error && <div className="ealert ealert-error">{error}</div>}
      {success && <div className="ealert ealert-success">{success}</div>}

      <div className="tm-filters">
        <label className="tm-filter" style={{ cursor: 'pointer' }}>
          <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} style={{ marginRight: 6 }} />
          Show archived
        </label>
      </div>

      {loading ? (
        <p className="loading">Loading memberships…</p>
      ) : visible.length === 0 ? (
        <div className="empty-state">
          <p>No membership plans yet. Create one to start collecting fees.</p>
          <button className="ebtn ebtn-primary" style={{ marginTop: 12 }} onClick={openCreate}>+ New membership</button>
        </div>
      ) : (
        <div className="card-surface">
          <table className="tm-table">
            <thead>
              <tr>
                <th>Plan</th>
                <th>Charge</th>
                <th>Scope</th>
                <th>Visibility</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {visible.map(m => {
                const charge = describePlan(m);
                return (
                <tr key={m.membershipId} className={m.active ? '' : 'tm-row-archived'}>
                  <td><span className="tm-person-name">{m.name}</span></td>
                  <td>
                    <span className="tm-chip" style={{ marginRight: 6 }}>{charge.kind}</span>
                    {charge.summary}
                  </td>
                  <td>{m.teamId ? teamName(m.teamId) || 'Team' : m.ageGroup ? `Age ${m.ageGroup}` : <span className="tm-muted">Club-wide</span>}</td>
                  <td><span className="tm-chip">{m.visibility === 'public' ? 'Public' : 'Admin only'}</span></td>
                  <td>
                    <div className="tm-actions">
                      <button className="ebtn ebtn-secondary ebtn-sm" onClick={() => openEdit(m)}>Edit</button>
                      {m.active && <button className="ebtn ebtn-danger ebtn-sm" onClick={() => handleArchive(m)}>Archive</button>}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="tm-modal-overlay" onClick={closeModal}>
          <div className="tm-modal" onClick={e => e.stopPropagation()}>
            <div className="tm-modal-header">
              <h2>{editing ? 'Edit membership' : 'New membership'}</h2>
              <button type="button" className="tm-modal-close" onClick={closeModal}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="tm-modal-body">
                <label className="tm-field">
                  <span>Name *</span>
                  <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. U12 Training 2026/27" required />
                </label>
                <div className="tm-field">
                  <span>How parents pay</span>
                  <div className="tm-form-row">
                    <label className="tm-field" style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                      <input type="radio" name="billing" checked={form.billing === 'once'} onChange={() => setForm({ ...form, billing: 'once' })} />
                      One payment
                    </label>
                    <label className="tm-field" style={{ flexDirection: 'row', gap: 8, alignItems: 'center', opacity: gc?.monthlyReady || form.billing === 'monthly' ? 1 : 0.6 }}>
                      <input type="radio" name="billing" checked={form.billing === 'monthly'} disabled={!gc?.monthlyReady && form.billing !== 'monthly'}
                        onChange={() => setForm({ ...form, billing: 'monthly' })} />
                      Monthly for the season
                    </label>
                  </div>
                  {!gc?.monthlyReady && form.billing !== 'monthly' && (
                    <p className="col-connect-sub" style={{ margin: 0 }}>Monthly payments open once card payments are set up under Settings → Payments.</p>
                  )}
                </div>
                <div className="tm-form-row">
                  <label className="tm-field">
                    <span>{form.billing === 'monthly' ? 'Each month (£) *' : 'Fee (£) *'}</span>
                    <input type="number" min="0" step="0.01" value={form.pricePounds} onChange={e => setForm({ ...form, pricePounds: e.target.value })} placeholder={form.billing === 'monthly' ? '30.00' : '150.00'} required />
                  </label>
                  {form.billing === 'monthly' && (
                    <>
                      <label className="tm-field">
                        <span>First payment</span>
                        <select value={form.firstMonth} onChange={e => setForm({ ...form, firstMonth: e.target.value })} required>
                          <option value="">Choose…</option>
                          {monthOptions().map(m => <option key={m} value={m}>1 {monthName(m, true)}</option>)}
                        </select>
                      </label>
                      <label className="tm-field">
                        <span>Last payment</span>
                        <select value={form.lastMonth} onChange={e => setForm({ ...form, lastMonth: e.target.value })} required>
                          <option value="">Choose…</option>
                          {monthOptions().map(m => <option key={m} value={m}>1 {monthName(m, true)}</option>)}
                        </select>
                      </label>
                    </>
                  )}
                </div>
                {form.billing === 'monthly' && form.firstMonth && form.lastMonth && monthsBetween(form.firstMonth, form.lastMonth) > 0 && (() => {
                  const n = monthsBetween(form.firstMonth, form.lastMonth);
                  const p = Math.round(parseFloat(form.pricePounds || '0') * 100);
                  return (
                    <div className="ealert ealert-info">
                      Parents pay <strong>£{(p / 100).toFixed(2)} on the 1st of each month, {monthName(form.firstMonth)} to {monthName(form.lastMonth)}: {n} payment{n === 1 ? '' : 's'}, £{((p * n) / 100).toFixed(2)} per player.</strong>{' '}
                      It stops by itself after the last one. A player who joins later starts on the next 1st and pays for the months left.
                      Stripe's card fee comes off each payment, and GrassrootsFC takes a small % on top.
                    </div>
                  );
                })()}


                <div className="tm-form-row">
                  <label className="tm-field">
                    <span>Scope to team (optional)</span>
                    <select value={form.teamId} onChange={e => setForm({ ...form, teamId: e.target.value })}>
                      <option value="">Club-wide</option>
                      {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </label>
                  <label className="tm-field">
                    <span>Age group (optional)</span>
                    <input type="text" value={form.ageGroup} onChange={e => setForm({ ...form, ageGroup: e.target.value })} placeholder="e.g. U12" />
                  </label>
                </div>
                <label className="tm-field">
                  <span>Visibility</span>
                  <select value={form.visibility} onChange={e => setForm({ ...form, visibility: e.target.value as MembershipVisibility })}>
                    <option value="admin-only">Admin only</option>
                    <option value="public">Public (shown on the club site)</option>
                  </select>
                </label>
              </div>
              <div className="tm-modal-actions">
                <button type="button" className="ebtn ebtn-secondary" onClick={closeModal}>Cancel</button>
                <button type="submit" className="ebtn ebtn-primary" disabled={saving}>
                  {saving ? 'Saving…' : editing ? 'Save changes' : 'Create membership'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
